import is from '@sindresorhus/is';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { REDUCED_MOTION_QUERY, TILE_REFLOW_DURATION } from './constants';
import { endResizeSession, type ResizeSession, startResizeSession, writeResizeWidth } from './resize-session';
import { computeTargetScrollLeft } from './scroll-helpers';
import { type TileWidthToggle, useTileResizeContext, useTilingEngineContext } from './TilingEngineContext';
import { tileWidthVar } from './tile-resize';
import type { ResizedWidth } from './types';

const MS_PER_SECOND = 1000;

// The JS form of the engine's ease-out-cubic width reflow.
const easeOutCubic = (progress: number): number => 1 - (1 - progress) ** 3;

// Where the surface rests once `width` applies: as far right as the surface goes without pushing
// the tile's left edge out of view. A maximized tile then fills the view; a restored last tile
// lands at the usual right-hand rest, and one with neighbours on its right stays visible.
// Measured by applying the width for one layout read, then putting the start state back.
const measureTarget = (
    session: ResizeSession,
    width: ResizedWidth | undefined,
): { readonly scrollLeft: number; readonly width: number } => {
    const { box, container } = session;
    const startScrollLeft = container.scrollLeft;

    writeResizeWidth(session, width);
    const tileStartScrollLeft = computeTargetScrollLeft(box, container, 'start', 0) ?? startScrollLeft;
    const target = {
        scrollLeft: Math.min(Math.max(tileStartScrollLeft, 0), container.scrollWidth - container.clientWidth),
        width: box.getBoundingClientRect().width,
    };
    writeResizeWidth(session, session.startWidth);
    container.scrollLeft = startScrollLeft;

    return target;
};

// Maximizing fills the visible surface with the tile; restoring hands the tile back to its own
// width. Width and scroll glide together frame by frame, so the tile grows into view instead of
// growing first and scrolling after.
export const useTileWidthToggleValue = (tileId: string, resizeKey: string | undefined): TileWidthToggle | null => {
    const { containerRef, tileBoxRefs } = useTilingEngineContext();
    const { resizedWidths, setResizedWidth } = useTileResizeContext();
    const isMaximized = is.nonEmptyString(resizeKey) && resizedWidths.get(resizeKey) === 'max';
    const finishRef = useRef<(() => void) | null>(null);

    useEffect(() => () => finishRef.current?.(), []);

    const toggle = useCallback(() => {
        const box = tileBoxRefs.current.get(tileId);
        const container = containerRef.current;

        if (!is.nonEmptyString(resizeKey) || is.undefined(box) || is.null(container) || !is.null(finishRef.current)) {
            return;
        }

        const width = isMaximized ? undefined : 'max';
        const session = startResizeSession(container, box, tileWidthVar(resizeKey));
        const startScrollLeft = container.scrollLeft;
        const target = measureTarget(session, width);
        const durationMs = globalThis.matchMedia(REDUCED_MOTION_QUERY).matches
            ? 0
            : TILE_REFLOW_DURATION * MS_PER_SECOND;
        const startTime = performance.now();
        let frameId: number | undefined;

        // The committed width lands only now: the surface variable it renders would cut the glide short.
        const finish = (): void => {
            if (!is.undefined(frameId)) {
                cancelAnimationFrame(frameId);
            }

            finishRef.current = null;
            writeResizeWidth(session, width);
            container.scrollLeft = target.scrollLeft;
            endResizeSession(session);
            setResizedWidth(resizeKey, width);
        };

        const step = (now: number): void => {
            const progress = durationMs === 0 ? 1 : Math.min(Math.max((now - startTime) / durationMs, 0), 1);

            if (progress === 1) {
                finish();
                return;
            }

            const eased = easeOutCubic(progress);
            writeResizeWidth(session, session.startWidth + (target.width - session.startWidth) * eased);
            container.scrollLeft = startScrollLeft + (target.scrollLeft - startScrollLeft) * eased;
            frameId = requestAnimationFrame(step);
        };

        finishRef.current = finish;
        frameId = requestAnimationFrame(step);
    }, [containerRef, isMaximized, resizeKey, setResizedWidth, tileBoxRefs, tileId]);

    return useMemo(
        () => (is.nonEmptyString(resizeKey) ? { isMaximized, toggle } : null),
        [isMaximized, resizeKey, toggle],
    );
};
